import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { COUNTRIES, CountryCode } from 'lib/constants/locales';
import PiiAttributes from 'lib/interfaces/PiiAttributes';
import {
  CountryCodeLower,
  GovernmentIdCaptureOption,
  RequiredDevice,
  VerificationConfigGovernmentId,
} from 'lib/interfaces/verificationConfig';
import { isMacSafari, supportsInputCapture } from 'lib/utils/device';
import { HolderClient } from 'verify/lib/clients/HolderClient';
import { Inquiry } from 'verify/models/inquiry';
import { Template } from 'verify/models/template';

export const getDefaultCountryCode = (
  template: Template,
  prefill: Partial<Record<PiiAttributes, string>>,
  inferredCountryCode: string,
): string | undefined => {
  if (template == null) {
    return undefined;
  }

  const enabledCountryCodes = template.attributes['enabled-country-codes'];
  const prefillCountryCode = (prefill || {})['country-code'];

  if (
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    (enabledCountryCodes.length === 0 && prefillCountryCode in COUNTRIES) ||
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    enabledCountryCodes.includes(prefillCountryCode as CountryCode)
  ) {
    return prefillCountryCode;
  }

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  if (prefillCountryCode == null && enabledCountryCodes.length === 1) {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return enabledCountryCodes[0];
  }

  if (
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    (enabledCountryCodes.length === 0 && inferredCountryCode in COUNTRIES) ||
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    enabledCountryCodes.includes(inferredCountryCode as CountryCode)
  ) {
    return inferredCountryCode;
  }
};

export const getSupportedGovernmentIdCaptureOptions = (
  config: VerificationConfigGovernmentId,
  countryCode: CountryCodeLower,
  governmentIdClass: GovernmentIdClass,
  hasWebCamera: boolean,
): GovernmentIdCaptureOption[] => {
  const {
    'enabled-capture-options-desktop': enabledCaptureOptionsDesktop,
    'enabled-capture-options-mobile': enabledCaptureOptionsMobile,
    'enabled-id-classes-by-country': enabledIdClassesByCountry,
  } = config;

  const governmentIdEnabledIdClassByCountryEntry =
    (enabledIdClassesByCountry || {})[countryCode] ?? [];
  const idSetup = governmentIdEnabledIdClassByCountryEntry.find(
    (v) => v.class === governmentIdClass,
  );
  // "Other" gov ID (which we don't verify) doesn't have an ID setup, so mobile isn't needed.
  const noMobileRequirement =
    idSetup == null || idSetup['requires-device'] !== RequiredDevice.Mobile;

  const isMobile = supportsInputCapture();
  const enabledCaptureOptions =
    (isMobile ? enabledCaptureOptionsMobile : enabledCaptureOptionsDesktop) || [];

  // Mac safari (not chrome) actually has good enough resolution.
  const sufficientCameraQuality = noMobileRequirement || isMacSafari() || isMobile;
  const showWebCameraOption =
    hasWebCamera &&
    // Barcode scan requires mobile device-level cameras
    sufficientCameraQuality &&
    // Checked separately to ensure native camera is turned off
    enabledCaptureOptions.includes(GovernmentIdCaptureOption.WebCamera);

  // For some reason, the FileReader API is not always available in WkWebView.
  const supportsFileUpload = window.FileReader != null;

  // These are listed in the order they appear (top to bottom on page)
  return [
    {
      option: GovernmentIdCaptureOption.WebCamera,
      condition: showWebCameraOption,
    },
    {
      option: GovernmentIdCaptureOption.MobileCamera,
      condition: !showWebCameraOption && isMobile,
    },
    {
      option: GovernmentIdCaptureOption.Upload,
      condition: supportsFileUpload,
    },
    {
      option: GovernmentIdCaptureOption.DeviceHandoff,
      condition: true,
    },
  ]
    .filter((opt) => opt.condition && enabledCaptureOptions.includes(opt.option))
    .map((opt) => opt.option);
};

// TODO: [PER-6006] Remove fork when cleaning up migration
// Note that relationships can be undefined if inquiry itself is in `included`.
// This should never happen for dynamic flow inquiries (we never fetch verifications).
export function isDynamicFlowInquiry(inquiry: Inquiry | undefined): boolean {
  return inquiry?.relationships?.['inquiry-template-version']?.data != null;
}

/**
 * Always show complete button for non-hosted flows.
 * For hosted flows, only show complete button if redirect-uri is present.
 * rename flowHasCompleteAction
 */
export function flowHasCompleteAction(inquiry: Inquiry): boolean {
  // Always show for non-hosted flows
  if (!HolderClient.isHosted) {
    return true;
  } else {
    return !!HolderClient.inquiryParams['redirect-uri'] || !!inquiry.attributes['redirect-uri'];
  }
}
